<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { t } from '../i18n';
import { readUiState, writeUiState } from '../uiState';
import { CHIP_ROW, PANEL, chipClass } from '../ui';
import {
  DEFAULT_STEP, STEPS, averageDays, daysLabel, formatDay, formatLevel, formatRub, formatShortRub, formatSignedLevel, formatStep, pluralWith,
  withFrom,
} from '../achievements';

// Block 2 of «Достижения» (docs/achievements/SPEC.md, decision 8 and «Проект v2»): how many days each equal step
// of capital took and what it was made of — money put in and what the market added. The conclusion in one
// phrase; an outlier is cut with its number; steps there on the start are one grey strip; steps reached on one
// day are a group (stubs after the first); a step where the market gave more than half is green; the step under
// way is dashed and chosen first. Tap, drag or arrows choose a bar.
const props = defineProps({
  // step → track from the server
  steps: {
    type: Object,
    required: true,
  },
  start: {
    type: String,
    required: true,
  },
  // Capital now: the default step is 100 000 from 500 000 ₽, 10 000 below
  capital: {
    type: Number,
    default: 0,
  },
});

// The portfolio line color of the growth chart, checked on the dark surface (docs/performance/SPEC.md, decision 21).
const COLOR = '#3987e5';
// Money (decision 2 of docs/design/SPEC.md): a step the market made more than half of.
const MARKET = '#34d399';
const STUB = '#64748b';
const HEIGHT = 168;
const TOP = 26;
const BOTTOM = 22;
const MIN_SLOT = 18;
const MAX_SLOT = 48;
const GAP = 2;

const stored = Number(readUiState('achievements.step', 0));
const step = ref(STEPS.includes(stored) ? stored : (props.capital >= 500000 ? DEFAULT_STEP : STEPS[0]));
watch(step, (value) => writeUiState('achievements.step', value));

const levels = computed(() => withFrom(props.steps[step.value], props.start));
const atStart = computed(() => levels.value.filter((l) => l.at_start));

// Bars: steps reached after the start, then the one under way.
const bars = computed(() => {
  const track = props.steps[step.value];
  const out = levels.value.filter((l) => !l.at_start).map((l) => ({ ...l, going: false }));
  if (track?.next) {
    out.push({
      level: track.next.level, days: track.next.days, from: track.next.from, date: null, going: true,
      remaining: track.next.remaining, invested: track.next.invested, market: track.next.market, group: 1,
    });
  }
  return out;
});

// The market made more than half of the step: it grows by itself.
function byMarket(bar) {
  return !bar.going && !bar.same_day && bar.market > 0 && bar.market > bar.invested;
}
const anyByMarket = computed(() => bars.value.some(byMarket));

// «Первые 100 тыс. — 1262 дня, последние 5 шагов — по 9 дн.» when the first step is the outlier;
// «Последние 5 шагов — в среднем 12 дней» otherwise; none yet — the first is under way.
const conclusion = computed(() => {
  const average = averageDays(levels.value);
  if (!average) {
    return t('achievements.firstStep', { step: formatStep(step.value) });
  }
  const done = bars.value.filter((b) => !b.going);
  const rest = averageDays(done.slice(1));
  if (scale.value.cut && done.length > 1 && done[0].days > top.value && rest) {
    return pluralWith(rest.count, 'achievements.firstVsLast', {
      step: formatStep(step.value), first: daysLabel(done[0].days), days: daysLabel(rest.days),
    });
  }
  if (average.count === 1) {
    return t('achievements.averageOne', { days: daysLabel(average.days) });
  }
  return average.count < 5
    ? pluralWith(average.count, 'achievements.averageAll', { days: daysLabel(average.days) })
    : t('achievements.average', { days: daysLabel(average.days) });
});

const selectedIndex = ref(null);
watch([step, () => props.steps], () => { selectedIndex.value = null; });
const current = computed(() => selectedIndex.value ?? bars.value.length - 1);
const selected = computed(() => bars.value[current.value] ?? null);

// Width of the scroll box: few bars get up to 48 px each, many get 18 px and scroll.
const scroller = ref(null);
const width = ref(320);
let observer = null;
onMounted(() => {
  observer = new ResizeObserver(([entry]) => { width.value = entry.contentRect.width; });
  if (scroller.value) {
    observer.observe(scroller.value);
  }
});
onUnmounted(() => observer?.disconnect());

const startSlots = computed(() => (atStart.value.length > 0 ? 1 : 0));
const slot = computed(() => Math.min(MAX_SLOT, Math.max(MIN_SLOT, width.value / Math.max(1, bars.value.length + startSlots.value))));
const offset = computed(() => startSlots.value * slot.value);
const chartWidth = computed(() => offset.value + slot.value * bars.value.length);
const scrollable = computed(() => chartWidth.value > width.value + 1);

watch([chartWidth, step], async () => {
  await nextTick();
  if (scroller.value) {
    scroller.value.scrollLeft = scroller.value.scrollWidth;
  }
}, { immediate: true });

// One step far longer than the rest (the first 100 000 in 1262 days, then 4–30) would flatten them all: the
// scale follows the second longest, the outlier reaches the top with a break and its number.
const scale = computed(() => {
  const days = bars.value.map((b) => b.days ?? 0).sort((a, b) => b - a);
  const max = days[0] ?? 0;
  const second = days[1] ?? 0;
  const cut = second > 0 && max > 3 * second;
  return { cut, cap: Math.max(1, cut ? second * 1.2 : max) };
});

// Two or three round ticks of days.
const ticks = computed(() => {
  const unit = [1, 2, 5, 10, 20, 30, 50, 100, 200, 500, 1000, 2000].find((u) => scale.value.cap / u <= 3) ?? 5000;
  const out = [];
  for (let v = 0; v < scale.value.cap + unit && out.length < 4; v += unit) {
    out.push(v);
  }
  return out;
});
const top = computed(() => ticks.value.at(-1) || 1);

function y(days) {
  return TOP + (HEIGHT - TOP - BOTTOM) * (1 - Math.min(days, top.value) / top.value);
}

function isCut(bar) {
  return scale.value.cut && bar.days > top.value;
}

function barX(index) {
  return offset.value + index * slot.value + GAP / 2;
}

// Bar with rounded top corners, anchored to the baseline.
function barPath(index, days) {
  const w = Math.max(1, slot.value - GAP);
  const x = barX(index);
  const base = y(0);
  const h = Math.max(2, base - y(days));
  const r = Math.min(4, w / 2, h);
  return `M${x},${base}V${base - h + r}Q${x},${base - h} ${x + r},${base - h}H${x + w - r}Q${x + w},${base - h} ${x + w},${base - h + r}V${base}Z`;
}

const labelEvery = computed(() => Math.ceil(48 / slot.value));

function showLabel(index) {
  return (bars.value.length - 1 - index) % labelEvery.value === 0;
}

function range(bar) {
  return `${formatLevel(bar.level - step.value)} → ${formatLevel(bar.level)}`;
}

// The selected step, or the group of one day it belongs to: its first bar carries the sums.
const head = computed(() => {
  let i = current.value;
  while (i > 0 && bars.value[i]?.same_day) {
    i--;
  }
  return bars.value[i] ?? null;
});

const info = computed(() => {
  const bar = head.value;
  if (!bar) {
    return '';
  }
  if (bar.going) {
    return t('achievements.stepGoing', { range: range(bar), days: daysLabel(bar.days), sum: formatRub(Math.max(0, bar.remaining)) });
  }
  const span = bar.group > 1
    ? pluralWith(bar.group, 'achievements.stepGroup', { from: formatLevel(bar.level - step.value), to: formatLevel(bar.level + (bar.group - 1) * step.value) })
    : range(bar);
  return t('achievements.stepDone', { range: span, days: daysLabel(bar.days), from: formatDay(bar.from), to: formatDay(bar.date) });
});

// What the selected step was made of: put in (blue) and the market (green; a loss eats into the blue, hatched).
const parts = computed(() => {
  const bar = head.value;
  if (!bar || bar.invested === null || bar.invested === undefined) {
    return null;
  }
  const invested = Math.max(0, bar.invested);
  const market = bar.market;
  const whole = market >= 0 ? invested + market : invested;
  if (whole <= 0) {
    return null;
  }
  return {
    invested: bar.invested,
    market,
    investedWidth: (invested / whole) * 100,
    marketWidth: (Math.abs(market) / whole) * 100,
  };
});

// Choosing a bar: a tap (a finger that moved less than 6 px — not the start of a sideways scroll); dragging
// along the chart when it does not scroll; hovering with a mouse; arrows and Home/End.
let dragging = false;
let downX = null;

function indexAt(event) {
  const box = event.currentTarget.getBoundingClientRect();
  const i = Math.floor((event.clientX - box.left - offset.value) / slot.value);
  return Math.min(bars.value.length - 1, Math.max(0, i));
}

function onDown(event) {
  dragging = !scrollable.value;
  downX = event.clientX;
  if (dragging) {
    selectedIndex.value = indexAt(event);
  }
}

function onMove(event) {
  if (dragging || event.pointerType === 'mouse') {
    selectedIndex.value = indexAt(event);
  }
}

function onUp(event) {
  if (downX !== null && Math.abs(event.clientX - downX) < 6) {
    selectedIndex.value = indexAt(event);
  }
  dragging = false;
  downX = null;
}

function onCancel() {
  dragging = false;
  downX = null;
}

// The mouse leaves: back to the step under way.
function onLeave(event) {
  onCancel();
  if (event.pointerType === 'mouse') {
    selectedIndex.value = null;
  }
}

function onKey(event) {
  const last = bars.value.length - 1;
  const moves = { ArrowLeft: current.value - 1, ArrowRight: current.value + 1, Home: 0, End: last };
  if (event.key in moves) {
    event.preventDefault();
    selectedIndex.value = Math.min(last, Math.max(0, moves[event.key]));
  }
}
</script>

<template>
  <section :class="PANEL">
    <h3 class="text-[1.0625rem] font-semibold text-slate-100 sm:text-base">
      {{ t('achievements.stepsTitle', { step: formatStep(step) }) }}
    </h3>
    <div
      :class="[CHIP_ROW, 'mt-2']"
      role="radiogroup"
      :aria-label="t('achievements.step')"
    >
      <button
        v-for="value in STEPS"
        :key="value"
        type="button"
        role="radio"
        :aria-checked="step === value"
        :class="chipClass(step === value)"
        @click="step = value"
      >
        {{ formatShortRub(value) }}
      </button>
    </div>
    <p class="mt-3 text-[0.9375rem] text-slate-200 sm:text-sm">
      {{ conclusion }}
    </p>

    <div class="mt-3 flex">
      <!-- Day ticks stay in place while the bars scroll -->
      <div
        class="relative w-9 shrink-0 text-[0.6875rem] tabular-nums text-slate-500"
        :style="{ height: `${HEIGHT}px` }"
        aria-hidden="true"
      >
        <span class="absolute right-1.5 top-0">{{ t('achievements.daysAxis') }}</span>
        <span
          v-for="tick in ticks"
          :key="tick"
          class="absolute right-1.5 -translate-y-1/2"
          :style="{ top: `${y(tick)}px` }"
        >{{ tick }}</span>
      </div>
      <div
        ref="scroller"
        class="min-w-0 flex-1 overflow-x-auto [scrollbar-width:none]"
      >
        <svg
          :width="chartWidth"
          :height="HEIGHT"
          class="block cursor-pointer rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
          :class="scrollable ? 'touch-auto' : 'touch-pan-y'"
          tabindex="0"
          role="img"
          :aria-label="t('achievements.stepsTitle', { step: formatStep(step) })"
          @pointerdown="onDown"
          @pointermove="onMove"
          @pointerup="onUp"
          @pointercancel="onCancel"
          @pointerleave="onLeave"
          @keydown="onKey"
        >
          <line
            v-for="tick in ticks"
            :key="tick"
            x1="0"
            :x2="chartWidth"
            :y1="y(tick)"
            :y2="y(tick)"
            stroke="#1e293b"
            stroke-width="1"
          />
          <!-- Steps already there on the start: one grey strip on the zero line -->
          <g v-if="atStart.length > 0">
            <rect
              :x="GAP / 2"
              :y="y(0) - 2"
              :width="Math.max(1, offset - GAP)"
              height="2"
              fill="#475569"
            />
          </g>
          <rect
            v-if="selected"
            :x="offset + current * slot"
            :y="0"
            :width="slot"
            :height="HEIGHT - BOTTOM + 4"
            fill="#1e293b"
            opacity="0.6"
          />
          <g
            v-for="(bar, index) in bars"
            :key="bar.level"
          >
            <path
              v-if="bar.going"
              :d="barPath(index, bar.days)"
              :fill="COLOR"
              fill-opacity="0.15"
              :stroke="COLOR"
              stroke-width="1.5"
              stroke-dasharray="3 2"
            />
            <path
              v-else-if="bar.same_day"
              :d="barPath(index, 0)"
              :fill="STUB"
            />
            <path
              v-else
              :d="barPath(index, bar.days)"
              :fill="byMarket(bar) ? MARKET : COLOR"
            />
            <!-- The cut outlier: a break across the bar and its number on top -->
            <template v-if="isCut(bar)">
              <path
                :d="`M${barX(index) - 1},${TOP + 14} l${slot - GAP + 2},-4 M${barX(index) - 1},${TOP + 19} l${slot - GAP + 2},-4`"
                class="stroke-slate-900"
                stroke-width="2.5"
              />
              <text
                :x="barX(index) + (slot - GAP) / 2"
                :y="TOP - 5"
                text-anchor="middle"
                class="fill-slate-300 text-[0.625rem] tabular-nums"
              >{{ bar.days }}</text>
            </template>
            <text
              v-else-if="index === current && !bar.same_day"
              :x="barX(index) + (slot - GAP) / 2"
              :y="y(bar.days) - 5"
              text-anchor="middle"
              class="fill-slate-200 text-[0.6875rem] tabular-nums"
            >{{ bar.days }}</text>
            <text
              v-if="showLabel(index)"
              :x="barX(index) + (slot - GAP) / 2"
              :y="HEIGHT - 6"
              text-anchor="middle"
              class="fill-slate-500 text-[0.625rem]"
            >{{ formatShortRub(bar.level) }}</text>
          </g>
        </svg>
      </div>
    </div>
    <p
      v-if="atStart.length > 0"
      class="mt-1 text-[0.75rem] text-slate-500"
    >
      {{ t('achievements.atStartRange', { level: formatLevel(atStart.at(-1).level) }) }}
    </p>
    <p
      class="mt-2 min-h-[2.5rem] text-[0.8125rem] tabular-nums text-slate-300 sm:min-h-[1.25rem] sm:text-xs"
      aria-live="polite"
    >
      {{ info }}
    </p>
    <template v-if="parts">
      <div
        class="mt-2 flex h-2 overflow-hidden rounded-full bg-slate-800"
        aria-hidden="true"
      >
        <span
          class="relative block h-full"
          :style="{ width: `${parts.investedWidth}%`, backgroundColor: COLOR }"
        >
          <!-- A market loss: the part of the money it ate, hatched over the blue -->
          <span
            v-if="parts.market < 0"
            class="absolute inset-y-0 right-0 bg-[repeating-linear-gradient(135deg,rgba(251,113,133,0.6)_0_3px,transparent_3px_6px)]"
            :style="{ width: `${(parts.marketWidth / parts.investedWidth) * 100}%` }"
          />
        </span>
        <span
          v-if="parts.market > 0"
          class="block h-full"
          :style="{ width: `${parts.marketWidth}%`, backgroundColor: MARKET }"
        />
      </div>
      <p class="mt-1.5 flex flex-wrap items-center gap-x-3 text-[0.8125rem] tabular-nums text-slate-400 sm:text-xs">
        <span class="flex items-center gap-1.5">
          <span
            class="size-2 rounded-full"
            :style="{ backgroundColor: COLOR }"
            aria-hidden="true"
          />
          {{ t('achievements.put', { sum: formatRub(parts.invested) }) }}
        </span>
        <span class="flex items-center gap-1.5">
          <span
            class="size-2 rounded-full"
            :class="parts.market < 0 ? 'bg-rose-400' : ''"
            :style="parts.market < 0 ? null : { backgroundColor: MARKET }"
            aria-hidden="true"
          />
          <span :class="parts.market < 0 ? 'text-rose-400' : ''">{{ t('achievements.marketGave', { sum: formatSignedLevel(parts.market) }) }}</span>
        </span>
      </p>
    </template>
    <p
      v-if="anyByMarket"
      class="mt-2 flex items-center gap-1.5 text-[0.75rem] text-slate-500"
    >
      <span
        class="size-2 rounded-full"
        :style="{ backgroundColor: MARKET }"
        aria-hidden="true"
      />
      {{ t('achievements.marketLegend') }}
    </p>
  </section>
</template>
